import type { Funnel } from "schemas/generated/funnel";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import Tooltip from "@replo/design-system/components/tooltip/Tooltip";

type EditableFunnelNameProps = {
  funnel: Pick<Funnel, "id" | "name">;
  projectId: string;
  isRenaming: boolean;
  setIsRenaming: (isRenaming: boolean) => void;
  inputClassName?: string;
  titleClassName?: string;
};

export default function EditableFunnelName({
  funnel,
  projectId,
  isRenaming,
  setIsRenaming,
  inputClassName,
  titleClassName,
}: EditableFunnelNameProps): React.ReactNode {
  const [funnelName, setFunnelName] = React.useState(funnel.name);

  const { mutate: updateFunnelName } = trpc.funnel.update.useMutation({
    onSuccess: () => {
      void trpcUtils.funnel.find.invalidate();
      void trpcUtils.funnel.list.invalidate();
    },
  });

  const handleSave = () => {
    if (funnelName.trim() === "") {
      setFunnelName(funnel.name);
      setIsRenaming(false);
      return;
    }

    if (funnelName !== funnel.name) {
      updateFunnelName({
        projectId,
        funnelId: funnel.id,
        name: funnelName,
      });
    }
    setIsRenaming(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setFunnelName(funnel.name);
      setIsRenaming(false);
    }
  };

  // Update local state when funnel name changes
  React.useEffect(() => {
    setFunnelName(funnel.name);
  }, [funnel.name]);

  if (isRenaming) {
    return (
      <Input
        size="base"
        type="text"
        value={funnelName}
        onChange={(e) => {
          e.stopPropagation();
          setFunnelName(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleSave}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        autoFocus
        layoutClassName={inputClassName}
      />
    );
  }

  return (
    <Tooltip content={funnelName} triggerAsChild>
      <span
        className={titleClassName}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsRenaming(true);
        }}
      >
        {funnelName}
      </span>
    </Tooltip>
  );
}
